import React, { useEffect, useMemo, useState } from 'react';
import { DoranRangePicker, type DateRange } from '@doranjs/react';
import { formatValue, toDoranDate } from '@doranjs/core';
import { useMediaQuery, useTheme } from '@mui/material';
import { immediateToast } from 'utils/toast';
import './doran-date-picker.scss';
import { useDoranFormikBridge } from './doran-field-shell';
import {
    DoranGoToToday,
    DoranModalDialog,
    DoranModalTrigger,
} from './doran-modal-field';

const MAX_RANGE_YEARS = 1;
const RANGE_ERROR_MESSAGE = 'بازه انتخاب شده بیشتر از 1 سال است';

const emptyRange = (): DateRange => ({ start: null, end: null });

const arrayToRange = (value: any): DateRange => ({
    start: toDoranDate(Array.isArray(value) ? value[0] : null),
    end: toDoranDate(Array.isArray(value) ? value[1] : null),
});

const rangeToArray = (range: DateRange): string[] => {
    if (!range.start || !range.end) return [];
    return [
        String(formatValue(range.start, 'YYYY/MM/DD') ?? ''),
        String(formatValue(range.end, 'YYYY/MM/DD') ?? ''),
    ];
};

const rangeToDisplay = (value: any): string => {
    if (!Array.isArray(value)) return '';
    const [start, end] = value;
    if (start && end) return `${start} تا ${end}`;
    return start || end || '';
};

const isRangeLongerThanAllowed = (range: DateRange): boolean => {
    if (!range.start || !range.end) return false;

    const start = range.start.toGregorian();
    const end = range.end.toGregorian();
    const maxEnd = new Date(start.getTime());
    maxEnd.setFullYear(maxEnd.getFullYear() + MAX_RANGE_YEARS);
    return end.getTime() > maxEnd.getTime();
};

export default function PersianDatePickerRange(props: any): JSX.Element {
    const noTextField = props?.noTextField;

    if (noTextField) {
        return <StandaloneRangeDialog {...props} />;
    }

    return <FormikRangePicker {...props} />;
}

const FormikRangePicker = (props: any): JSX.Element => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const bridge = useDoranFormikBridge(props);
    const {
        label,
        icon,
        disabled,
        showClearBtn,
        mindate,
        maxdate,
        minDate,
        maxDate,
        ltr,
    } = props;
    const [open, setOpen] = useState(false);
    const externalRange = useMemo(
        () => arrayToRange(bridge.value),
        [bridge.value],
    );
    const [draftRange, setDraftRange] = useState<DateRange>(externalRange);
    const min = useMemo(
        () => toDoranDate(mindate ?? minDate),
        [mindate, minDate],
    );
    const max = useMemo(
        () => toDoranDate(maxdate ?? maxDate),
        [maxdate, maxDate],
    );

    useEffect(() => {
        if (!open) setDraftRange(externalRange);
    }, [externalRange.start?.epochMs, externalRange.end?.epochMs, open]);

    const openCalendar = () => {
        setDraftRange(externalRange);
        setOpen(true);
    };

    const selectRange = (nextRange: DateRange) => {
        setDraftRange(nextRange);
        if (!nextRange.start || !nextRange.end) return;
        if (
            max &&
            (nextRange.start.epochMs > max.epochMs ||
                nextRange.end.epochMs > max.epochMs)
        ) {
            return;
        }

        if (isRangeLongerThanAllowed(nextRange)) {
            immediateToast('error', { message: RANGE_ERROR_MESSAGE });
            return;
        }

        bridge.setValue(rangeToArray(nextRange));
        setOpen(false);
    };

    const clearRange = () => {
        bridge.setValue([]);
        setDraftRange(emptyRange());
    };

    return (
        <>
            <DoranModalTrigger
                name={bridge.name}
                label={label || 'بازه تاریخ'}
                value={rangeToDisplay(bridge.value)}
                icon={icon}
                disabled={Boolean(disabled)}
                invalid={bridge.invalid}
                helperText={bridge.helperText}
                open={open}
                onOpen={openCalendar}
                onClear={showClearBtn === false ? undefined : clearRange}
                onBlur={bridge.onBlur}
                ltr={ltr !== false}
            />

            <DoranModalDialog
                open={open}
                title={label || 'انتخاب بازه تاریخ'}
                onClose={() => setOpen(false)}
                wide={isDesktop}>
                <div
                    className={`project-doran-dialog__calendar-wrap project-doran-dialog__calendar-wrap--range${
                        props?.chartRangeSize ? ' project-doran-dialog__calendar-wrap--chart-range' : ''
                    }`}>
                    <DoranRangePicker
                        value={draftRange}
                        onChange={selectRange}
                        numberOfMonths={isDesktop ? 2 : 1}
                        headerMode="dropdown"
                        footerActions={[]}
                        slots={{ footer: <DoranGoToToday /> }}
                        {...(min ? { min } : {})}
                        {...(max ? { max } : {})}
                    />
                </div>
            </DoranModalDialog>
        </>
    );
};

const StandaloneRangeDialog = (props: any): JSX.Element => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const noTextField = props?.noTextField ?? {};
    const open = Boolean(noTextField?.isOpenCalender);
    const setOpen = noTextField?.setDatepickerOpen;
    const min = toDoranDate(props?.mindate ?? props?.minDate);
    const max = toDoranDate(props?.maxdate ?? props?.maxDate);
    const [draftRange, setDraftRange] = useState<DateRange>(emptyRange());

    useEffect(() => {
        if (open) setDraftRange(emptyRange());
    }, [open]);

    const close = () => setOpen?.(false);

    const selectRange = (nextRange: DateRange) => {
        setDraftRange(nextRange);
        if (!nextRange.start || !nextRange.end) return;
        if (
            max &&
            (nextRange.start.epochMs > max.epochMs ||
                nextRange.end.epochMs > max.epochMs)
        ) {
            return;
        }

        if (isRangeLongerThanAllowed(nextRange)) {
            immediateToast('error', { message: RANGE_ERROR_MESSAGE });
            return;
        }

        noTextField?.onChange?.(rangeToArray(nextRange));
        close();
    };

    return (
        <DoranModalDialog
            open={open}
            title="انتخاب بازه تاریخ"
            onClose={close}
            wide={isDesktop}>
            <div
                className="project-doran-dialog__calendar-wrap project-doran-dialog__calendar-wrap--range project-doran-dialog__calendar-wrap--chart-range">
                <DoranRangePicker
                    value={draftRange}
                    onChange={selectRange}
                    numberOfMonths={isDesktop ? 2 : 1}
                    headerMode="dropdown"
                    footerActions={[]}
                    slots={{ footer: <DoranGoToToday /> }}
                    {...(min ? { min } : {})}
                    {...(max ? { max } : {})}
                />
            </div>
        </DoranModalDialog>
    );
};
